import axios from 'axios';
import get from 'lodash/get';
import { getBearerIdTokenHeader } from '../../services/auth';
import { Amplify } from '@aws-amplify/core';
import config from '../../config';

Amplify.configure(config);

const baseURL =
  process.env.REACT_APP_WELDIQ_API_BASE_URL ||
  'http://localhost:8080/api';

const axiosConfig = axios.create({
  baseURL: baseURL.endsWith('/') ? baseURL : `${baseURL}/`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

/**
 * Retry a 401 at most once with a fresh token.
 * Never mutates global axios.defaults (avoids cross-client auth pollution).
 */
export async function handleErroredResponse(error) {
  const originalRequest = get(error, ['response', 'config'], undefined);
  const statusCode = get(error, ['response', 'status'], undefined);
  const errData = get(error, ['response', 'data'], undefined);
  const errName = get(error, ['response', 'data', 'name'], undefined);

  if (
    statusCode === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;
    // Prefer retrying on explicit token expiry; still allow a single refresh attempt
    // for other 401s so Cognito session rotation continues to work.
    const token = await getBearerIdTokenHeader();
    if (token) {
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = token;
      return axiosConfig(originalRequest);
    }
    void errName;
  }

  if (errData) {
    return Promise.reject(errData);
  }
  return Promise.reject(error.message || error);
}

axiosConfig.interceptors.request.use(
  async (aConfig) => {
    const authToken = await getBearerIdTokenHeader();
    if (authToken) {
      aConfig.headers.Authorization = authToken;
    }
    return aConfig;
  },
  (error) => Promise.reject(error),
);

axiosConfig.interceptors.response.use(
  (response) => response,
  async (error) => handleErroredResponse(error),
);

export default axiosConfig;
